import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { del, get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './AIManagement.css';

type CharacterNameRecord = {
  id: number;
  name: string;
  gender: string;
  genres: string[];
  factions: string[];
  tags: string[];
  enabled: boolean;
  source: string;
  note: string;
};

type ListResponse = {
  data: CharacterNameRecord[];
  total: number;
  library_total: number;
  enabled_total: number;
  page: number;
  page_size: number;
};

const GENRES = ['都市', '悬疑', '科幻', '历史', '古言', '仙侠', '玄幻', '校园', '职场', '现实'];
const FACTIONS = ['主角', '亲属', '盟友', '师长', '谜团', '敌对'];
const GENDERS = ['男', '女', '中性'];

const CharacterNameLibraryPage: React.FC = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState<CharacterNameRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState<CharacterNameRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [genre, setGenre] = useState<string>();
  const [faction, setFaction] = useState<string>();
  const [gender, setGender] = useState<string>();
  const [enabled, setEnabled] = useState<boolean | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [enabledTotal, setEnabledTotal] = useState(0);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (genre) params.set('genre', genre);
      if (faction) params.set('faction', faction);
      if (gender) params.set('gender', gender);
      if (enabled !== undefined) params.set('enabled', String(enabled));
      const data = await get<ListResponse>(`${API_ENDPOINTS.ai_management.characterNameLibrary}?${params}`);
      setRows(data.data);
      setTotal(data.total);
      setLibraryTotal(data.library_total);
      setEnabledTotal(data.enabled_total);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载人物名字库失败');
    } finally {
      setLoading(false);
    }
  }, [enabled, faction, gender, genre, keyword, page, pageSize]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openEditor = (record?: CharacterNameRecord) => {
    setEditing(record || null);
    setModalOpen(true);
    form.setFieldsValue(record ? {
      ...record,
      genres: record.genres || [],
      factions: record.factions || [],
      tags: record.tags || [],
    } : { gender: '中性', enabled: true, genres: [], factions: [], tags: [] });
  };

  const saveRecord = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await put(API_ENDPOINTS.ai_management.characterNameDetail(editing.id), values);
      } else {
        await post(API_ENDPOINTS.ai_management.characterNameLibrary, values);
      }
      message.success(editing ? '姓名记录已更新' : '姓名已加入人物库');
      setModalOpen(false);
      form.resetFields();
      await loadRows();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (record: CharacterNameRecord, value: boolean) => {
    try {
      await put(API_ENDPOINTS.ai_management.characterNameDetail(record.id), { enabled: value });
      message.success(value ? '已启用' : '已停用');
      await loadRows();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新失败');
    }
  };

  const removeRecord = async (record: CharacterNameRecord) => {
    try {
      await del(API_ENDPOINTS.ai_management.characterNameDetail(record.id));
      message.success('姓名记录已删除');
      await loadRows();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  const syncSeed = async () => {
    setSyncing(true);
    try {
      const result = await post<{ inserted: number; skipped: number; total: number }>(
        API_ENDPOINTS.ai_management.characterNameImport,
      );
      message.success(`同步完成：新增 ${result.inserted} 条，保留 ${result.skipped} 条，共 ${result.total} 条`);
      await loadRows();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'JSON同步失败');
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    { title: '姓名', dataIndex: 'name', width: 120, render: (value: string) => <strong>{value}</strong> },
    { title: '性别', dataIndex: 'gender', width: 80 },
    {
      title: '适用题材', dataIndex: 'genres', width: 200,
      render: (values: string[]) => <Space size={[4, 4]} wrap>{(values || []).map(value => <Tag key={value}>{value}</Tag>)}</Space>,
    },
    {
      title: '适用阵营', dataIndex: 'factions', width: 210,
      render: (values: string[]) => <Space size={[4, 4]} wrap>{(values || []).map(value => <Tag color="blue" key={value}>{value}</Tag>)}</Space>,
    },
    {
      title: '风格标签', dataIndex: 'tags',
      render: (values: string[]) => <Space size={[4, 4]} wrap>{(values || []).map(value => <Tag color="purple" key={value}>{value}</Tag>)}</Space>,
    },
    { title: '来源', dataIndex: 'source', width: 100, render: (value: string) => value === 'admin' ? '后台新增' : 'JSON种子' },
    {
      title: '启用', dataIndex: 'enabled', width: 80,
      render: (value: boolean, record: CharacterNameRecord) => (
        <Switch size="small" checked={value} onChange={checked => toggleEnabled(record, checked)} />
      ),
    },
    {
      title: '操作', key: 'actions', width: 150,
      render: (_: unknown, record: CharacterNameRecord) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditor(record)}>编辑</Button>
          <Popconfirm title={`确认删除“${record.name}”？`} onConfirm={() => removeRecord(record)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ai-management-page">
      <h1 className="page-title">人物名字库</h1>
      <Alert
        type="info"
        showIcon
        message="灵感和人物图谱共用同一名字库"
        description="系统会从题材、阵营标签匹配并按作品稳定随机取名；梗概已出现的人名会被强制映射到人物图谱。JSON种子可在新环境一键同步，后台停用或删除后不会再用于新人物。"
        style={{ marginBottom: 16 }}
      />

      <Space size={16} style={{ marginBottom: 16 }}>
        <Card size="small"><Statistic title="名字总数" value={libraryTotal} /></Card>
        <Card size="small"><Statistic title="当前启用" value={enabledTotal} /></Card>
        <Card size="small"><Statistic title="本次筛选" value={total} /></Card>
      </Space>

      <Card bordered={false} className="ai-section-card">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder="搜索姓名、标签或备注"
            style={{ width: 240 }}
            onSearch={value => { setKeyword(value); setPage(1); }}
          />
          <Select allowClear placeholder="题材" style={{ width: 120 }} options={GENRES.map(value => ({ value }))} onChange={value => { setGenre(value); setPage(1); }} />
          <Select allowClear placeholder="阵营" style={{ width: 120 }} options={FACTIONS.map(value => ({ value }))} onChange={value => { setFaction(value); setPage(1); }} />
          <Select allowClear placeholder="性别" style={{ width: 110 }} options={GENDERS.map(value => ({ value }))} onChange={value => { setGender(value); setPage(1); }} />
          <Select
            allowClear placeholder="状态" style={{ width: 110 }}
            options={[{ label: '已启用', value: true }, { label: '已停用', value: false }]}
            onChange={value => { setEnabled(value); setPage(1); }}
          />
          <Button icon={<ReloadOutlined />} onClick={loadRows}>刷新</Button>
          <Button icon={<SyncOutlined />} loading={syncing} onClick={syncSeed}>从JSON同步</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}>新增姓名</Button>
        </Space>

        <Table<CharacterNameRecord>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1150 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: value => `共 ${value} 条`,
            onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); },
          }}
        />
      </Card>

      <Modal
        title={editing ? `编辑姓名：${editing.name}` : '新增人物姓名'}
        open={modalOpen}
        confirmLoading={saving}
        onOk={saveRecord}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="2到4个汉字，如：林知遥" />
          </Form.Item>
          <Form.Item name="gender" label="性别倾向" rules={[{ required: true }]}>
            <Select options={GENDERS.map(value => ({ value }))} />
          </Form.Item>
          <Form.Item name="genres" label="适用题材">
            <Select mode="multiple" options={GENRES.map(value => ({ value }))} />
          </Form.Item>
          <Form.Item name="factions" label="适用阵营">
            <Select mode="multiple" options={FACTIONS.map(value => ({ value }))} />
          </Form.Item>
          <Form.Item name="tags" label="风格标签">
            <Select mode="tags" tokenSeparators={[',', '，']} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
          <Form.Item name="enabled" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CharacterNameLibraryPage;
